import { icon_magnifier } from 'components/base/images';
import CustomSpan from 'components/common/text/CustomSpan';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { color } from 'styles/utils';
import cx from 'classnames';
import CustomDatePicker from 'components/common/input/CustomDatePicker';
import MuiWrapper from 'components/common/input/MuiWrapper';
import { Grid, TextField } from '@material-ui/core';
import CustomMuiCheckbox from 'components/common/checkbox/CustomMuiCheckbox';
import useCheckOneInput from 'lib/hooks/useCheckOneInput';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import MuiButton from 'components/common/button/MuiButton';
import { useDidUpdateEffect } from 'lib/utils';
import NewPartnerModalContainer from 'containers/partner/NewPartnerModalContainer';
import { AppActions, NotificationActions, PartnerActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import { v4 as uuid } from 'uuid';
import { useHistory } from 'react-router-dom';
import { pageUrl, notificationsIconList } from 'lib/mapper';
import T from 'components/common/text/T';
import PageTitle from 'components/base/title/PageTitle';
import CustomPagination from 'components/common/pagination/CustomPagination';
import { PageSubTitle } from 'components/base/title/PageTitle';
import ListEmpty from 'components/common/list/ListEmpty';
import CircularLoading from 'components/base/loading/CircularLoading';

interface Notification {
  category: string;
  enrollDate: number;
  fromEmail: string;
  fromName: string;
  isRead: number; // 0, 1
  modifyDate: number | null; // unixstamp, null
  title: string;
  type: string;
  _id: string;
  params?: any;
}

interface NotificationsProps {
  notificationList: Notification[];
  isLoadSuccess: boolean;
  isLoadFailure: boolean;
  pagingData: object | any;
  page: { value: number; onChange: (e: any) => void; setValue: React.Dispatch<number> };
  period: { value: Date | null; onChange: (e: any) => void; setValue: React.Dispatch<Date> };
  keyword: { value: string; onChange: (e: any) => void; setValue: React.Dispatch<string> };
  checkedNotifications: { value: any; onChange: (e: any) => void; setValue: React.Dispatch<any> };
  onSearch: () => void;
  onRead: () => void;
  onDelete: () => void;
  onChangePage: (value: number) => void;
}

const Notifications = ({
  notificationList,
  isLoadSuccess,
  isLoadFailure,
  pagingData,
  page,
  period,
  keyword,
  checkedNotifications,
  onSearch,
  onRead,
  onDelete,
  onChangePage,
}: NotificationsProps) => {
  const history = useHistory();
  const { answerReceiveRequestedSuccess, invalidPartnerGroupcode, fetchNotificationsPending } =
    useShallowAppSelector(state => ({
      answerReceiveRequestedSuccess: state.partner.answerReceiveRequested.success,
      invalidPartnerGroupcode: state.partner?.invalidPartnerGroupcode || '',
      fetchNotificationsPending: state.notification.notifications.pending,
    }));
  const listIdData = notificationList?.map(item => item._id) || [];

  const { t } = useTranslation();
  const checkedNotificationsAll = useCheckOneInput(false);
  const [notiId, setNotiId] = useState('');

  // SECTION: function
  const handleCheckNotificationsAll = (e: any) => {
    const checkedValue = e.target.checked;
    checkedNotificationsAll.onChange(e);

    if (checkedValue) {
      checkedNotifications.setValue(new Set([...listIdData]));
    } else {
      checkedNotifications.setValue(new Set([]));
    }
  };

  const handleNavigateProject = ({
    notiId,
    isRead,
    projectCode,
  }: {
    notiId: string;
    isRead: number;
    projectCode: string;
  }) => {
    if (!notiId || !projectCode) return;
    if (!isRead) {
      NotificationActions.read_notifications_request({
        notificationIdArr: [notiId],
      });
    }

    history.push(`/project/detail/${projectCode}`);
  };

  const handleOpenPartnerModal = ({
    notiId,
    isRead,
    userGroupCode,
    partnerState,
  }: {
    notiId: string;
    isRead: number;
    userGroupCode: string;
    partnerState: number;
  }) => {
    console.log('call handleOpenPartnerModal id', notiId, userGroupCode, partnerState);
    if (!notiId || !partnerState || !userGroupCode) return;
    if (!isRead) {
      NotificationActions.read_notifications_request({
        notificationIdArr: [notiId],
      });
    }
    setNotiId(notiId);

    // open modal
    const pid = uuid();
    AppActions.add_popup({
      id: pid,
      isOpen: true,
      title: t('GLOBAL_NEW_PARTNER'),
      content: (
        <NewPartnerModalContainer
          receivedRequestGroupCode={userGroupCode}
          prevPartnerStateProp={partnerState}
          onClosePartnerModal={() => AppActions.remove_popup_delay({ id: pid, isOpen: false })}
        />
      ),
      isCloseIcon: true,
      hideButton: true,
      width: 'auto',
      contentCardStyle: {
        padding: 0,
        backgroundColor: 'transparent',
      },
    });
  };

  // SECTION: DidUpdate

  // 리스트 체크박스 변경 됐을 경우 checkAll 변경, if check all
  useDidUpdateEffect(() => {
    if (
      [...checkedNotifications.value].length &&
      [...checkedNotifications.value].length === notificationList.length
    ) {
      checkedNotificationsAll.setValue(true);
    } else {
      checkedNotificationsAll.setValue(false);
    }
  }, [checkedNotifications.value]);

  // when answerReceiveRequestedSuccess, delete notifications
  useDidUpdateEffect(() => {
    if (answerReceiveRequestedSuccess && notiId) {
      NotificationActions.remove_notification({ notiId });
      setNotiId('');
    }
  }, [!!answerReceiveRequestedSuccess, notiId]);

  // invalid partnerGroupCode, delete notifications
  useDidUpdateEffect(() => {
    if (!notificationList?.length || !invalidPartnerGroupcode) return;
    const matchingInvalidNotiId = notificationList.find(
      item => item?.params?.userGroupCode === invalidPartnerGroupcode,
    )?._id;

    if (matchingInvalidNotiId) {
      NotificationActions.delete_notifications_request({
        notificationIdArr: [matchingInvalidNotiId],
      });

      PartnerActions.set_invalid_parter_groupcode('');
    }
  }, [notificationList, invalidPartnerGroupcode]);

  return (
    <section>
      <PageTitle title={t('NOTIFICATION_TITLE')} />
      <StyledNotifications data-component-name="Notifications">
        <div className="notifications__title_container">
          <PageSubTitle title={t('NOTIFICATION_TITLE')} />
          <div className="notifications__title_btn_box">
            <MuiButton
              config={{
                width: 130,
              }}
              className="notifications__new_invoice_btn md "
              variant="outlined"
              color="primary"
              onClick={onRead}
            >
              <T>GLOBAL_READ</T>
            </MuiButton>
            <MuiButton
              config={{
                width: 130,
              }}
              className="notifications__new_invoice_btn md "
              variant="outlined"
              color="primary"
              onClick={onDelete}
            >
              <T>GLOBAL_DELETE</T>
            </MuiButton>
          </div>
        </div>

        <div className="notifications__content_container">
          <div className="notifications__filter_box">
            <CustomDatePicker
              type="rangeDate"
              fullWidth
              borderColor="#B5B7C1"
              height={32}
              fontSize={12}
              value={period.value}
              onChange={period.onChange}
              className={cx({
                error: period.value !== null ? !period.value : false,
              })}
              isClient={true}
            />
            {useMemo(
              () => (
                <MuiWrapper
                  className="notifications__search_wrapper sm"
                  config={{
                    borderColor: 'transparent',
                    activeBorderColor: 'transparent',
                    hoverBorderColor: 'transparent',
                  }}
                >
                  <>
                    <TextField
                      className="radius-md"
                      variant="outlined"
                      fullWidth
                      placeholder={t('PARTNER_SEARCH_PLACEHOLDER')}
                      value={keyword.value || ''}
                      onChange={keyword.onChange}
                      onKeyPress={e => e.key === 'Enter' && onSearch()}
                    />
                    <button className="btn-reset notifications__search_btn" onClick={onSearch}>
                      <img src={icon_magnifier} alt="search" />
                    </button>
                  </>
                </MuiWrapper>
              ),
              [keyword.value],
            )}
          </div>

          <div className="notifications__table_container">
            <div className="notifications__table">
              <div className="notifications__table_header">
                <div className="notifications__table_row header">
                  <div className="notifications__table_cell header">
                    <CustomMuiCheckbox
                      checked={checkedNotificationsAll.value}
                      onChange={handleCheckNotificationsAll}
                      padding={0}
                    />
                  </div>
                  <div className="notifications__table_cell header">
                    <CustomSpan fontColor={color.gray_week}>
                      <T>GLOBAL_NUM</T>
                    </CustomSpan>
                  </div>
                  <div className="notifications__table_cell header"></div>
                  <div className="notifications__table_cell header">
                    <T>GLOBAL_EMAIL</T>
                  </div>
                  <div className="notifications__table_cell header">
                    <T>GLOBAL_NAME</T>
                  </div>
                  <div className="notifications__table_cell header">
                    <T>GLOBAL_CONTENT</T>
                  </div>
                  <div className="notifications__table_cell header">
                    <T>GLOBAL_DATE</T>
                  </div>
                </div>
              </div>

              <div className="notifications__table_body">
                {notificationList?.length < 1 && fetchNotificationsPending && (
                  <div className="infinite-scroll-loading">
                    <CircularLoading />
                  </div>
                )}
                {!!isLoadSuccess &&
                  (notificationList.length > 0 ? (
                    notificationList.map((item, idx) => {
                      const iconSrc =
                        notificationsIconList.find(iconItem => iconItem.types.includes(item.type))
                          ?.src || '';
                      let partnerState = 0;
                      let isSignOutPartner = false;
                      const validPartnerParams =
                        (item.type === 'NEW_PARTNER_REQ' || item.type === 'PARTNER_ACCEPT') &&
                        item?.params?.userGroupCode;
                      if (item.type === 'NEW_PARTNER_REQ') partnerState = 3;
                      if (item.type === 'PARTNER_ACCEPT') partnerState = 1;
                      const hasProjectType = item.type.includes('PROJECT_');
                      const validProjectParams = hasProjectType && item?.params?.projectCode;
                      const isShowView = validPartnerParams || validProjectParams;
                      const hasDataType = item.type.includes('DATA_');
                      const hasAppType = item.type.includes('APP_');
                      const hasWarrantyType = item.type.includes('WARRANTY_');
                      const isShowStoreLink = item.category === 'STORE';
                      let relatedName = '';
                      if (item.category === 'PROJECT') {
                        relatedName = item?.params?.projectName;
                      }
                      if (item.category === 'PARTNER') {
                        if (!item.fromEmail || !item.fromName) {
                          isSignOutPartner = true;
                        }
                        relatedName = item.fromName;
                      }
                      if (item.category === 'STORE') {
                        relatedName = item?.params?.productName;
                      }
                      const title = t(`NOTIFICATION_EVENT_TYPE_${item.type}`);
                      const content = relatedName ? `${relatedName} ${title}` : title;

                      return (
                        <div
                          key={item._id}
                          className={cx('notifications__table_row', { unactive: item.isRead })}
                        >
                          <div className="notifications__table_cell">
                            <CustomMuiCheckbox
                              checked={checkedNotifications.value.has(item._id)}
                              onChange={() => checkedNotifications.onChange({ value: item._id })}
                              padding={0}
                            />
                          </div>
                          <div className="notifications__table_cell">
                            <CustomSpan fontColor={color.gray_week}>{idx + 1}</CustomSpan>
                          </div>
                          <div className="notifications__table_cell">
                            <img className="notifications__icon" src={iconSrc} />
                          </div>
                          <div className="notifications__table_cell">{item.fromEmail}</div>
                          <div className="notifications__table_cell">{item.fromName}</div>
                          <div className="notifications__table_cell">
                            <Grid container alignItems="center" justifyContent="space-between">
                              <Grid item xs={10} title={content}>
                                {content}
                              </Grid>
                              {(isShowView || isShowStoreLink) && !isSignOutPartner && (
                                <Grid item xs={2}>
                                  <MuiButton
                                    config={{
                                      width: 130,
                                    }}
                                    className="notifications__new_invoice_btn sm"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => {
                                      if (item.category === 'PROJECT') {
                                        return handleNavigateProject({
                                          notiId: item._id,
                                          isRead: item.isRead,
                                          projectCode: item?.params?.projectCode,
                                        });
                                      }
                                      if (item.category === 'PARTNER') {
                                        return handleOpenPartnerModal({
                                          notiId: item._id,
                                          isRead: item.isRead,
                                          userGroupCode: item?.params?.userGroupCode,
                                          partnerState,
                                        });
                                      }
                                      if (item.category === 'STORE') {
                                        if (hasDataType) {
                                          history.push(pageUrl.store.data);
                                        } else if (hasAppType) {
                                          history.push(pageUrl.store.app);
                                        } else if (hasWarrantyType) {
                                          history.push(pageUrl.store.warranty);
                                        } else {
                                          history.push(pageUrl.store.index);
                                        }
                                      }
                                    }}
                                  >
                                    {isShowView && <T>GLOBAL_VIEW</T>}
                                    {isShowStoreLink && <T>GLOBAL_LINK</T>}
                                  </MuiButton>
                                </Grid>
                              )}
                            </Grid>
                          </div>
                          <div className="notifications__table_cell">
                            <CustomSpan fontColor={color.gray_week}>
                              {moment(moment.unix(item.enrollDate)).format('YY.MM.DD HH:mm')}
                            </CustomSpan>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <ListEmpty text="NOTIFICATION_LIST_EMPTY" />
                  ))}
                {!!isLoadFailure && <ListEmpty text="GLOBAL_LIST_ERROR" isRefreshButton={true} />}
              </div>
            </div>
            {!!isLoadSuccess && notificationList.length > 0 && !!pagingData?.totalPage && (
              <CustomPagination
                totalPage={pagingData?.totalPage}
                currPage={page.value}
                onChange={onChangePage}
              />
            )}
          </div>
        </div>
      </StyledNotifications>
    </section>
  );
};

const StyledNotifications = styled.div`
  margin-top: 20px;
  padding: 40px 0 30px;
  height: 100%;
  background-color: white;
  border-radius: 15px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.16);

  .notifications__title_container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 30px;

    .notifications__title_btn_box {
      button + button {
        margin-left: 15px;
      }
    }
  }

  .notifications__content_container {
    padding: 0 30px;

    .notifications__filter_box {
      display: flex;
      column-gap: 15px;

      .datePicker__box {
        width: 225px;
        .ant-picker-input {
          width: 65px;
        }
      }

      .notifications__search_wrapper {
        position: relative;
        width: 350px;
        .MuiInputBase-root {
          background-color: #f4f5fa;
        }
        .notifications__search_btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          position: absolute;
          right: 0;
          top: 0;
          width: 32px;
          height: 32px;
          border-top-right-radius: 5px;
          border-bottom-right-radius: 5px;
          background-color: ${color.navy_blue};
          img {
            width: 18px;
          }
        }
      }
    }

    .notifications__table_container {
      margin-top: 20px;

      .muiPagination {
        margin-top: 30px;
      }

      .notifications__table {
        width: 100%;
        table-layout: fixed;
        .notifications__icon {
          width: 50px;
          height: 50px;
        }
        .notifications__table_row {
          display: flex;
          align-items: center;
        }
        .notifications__table_cell {
          &:nth-child(1) {
            width: 4%;
          }
          &:nth-child(2) {
            width: 4%;
          }
          &:nth-child(3) {
            width: 5%;
          }
          &:nth-child(4) {
            width: 13%;
          }
          &:nth-child(5) {
            width: 14%;
          }
          &:nth-child(6) {
            width: 65%;
          }
          &:nth-child(7) {
            width: 10%;
          }
        }

        .notifications__table_cell {
          padding: 0 10px;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
          &:nth-child(3) {
            text-overflow: initial;
          }
          &:nth-child(1),
          &:nth-child(2),
          &:nth-child(3),
          &:nth-child(7) {
            display: flex;
            align-items: center;
          }

          &.header {
            background-color: #f4f5fa;
            font-size: 15px;
            font-weight: 500;

            &:first-child {
              border-top-left-radius: 5px;
              border-bottom-left-radius: 5px;
            }
            &:last-child {
              border-top-right-radius: 5px;
              border-bottom-right-radius: 5px;
            }
          }
        }

        .notifications__table_header {
          .notifications__table_row {
            .notifications__table_cell {
              height: 40px;
              display: flex;
              align-items: center;
            }
          }
        }
        .notifications__table_body {
          position: relative;
          width: 100%;
          height: 600px;
          margin-bottom: 20px;
          .notifications__table_row {
            font-size: 13px;
            height: 60px;
            &.unactive {
              background-color: #f0f0f0;
            }
          }
        }
      }
    }
  }
`;

export default React.memo(Notifications);
